import { htmlSafe } from '@ember/template';
import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import EmberObject, { computed, action } from '@ember/object';

import BaseAnalytics from '../base-analytics';
import { FREELANCER_PROJECT_METRIC } from "../../../models/freelancer-project-metric";
import { FACTOID } from "../../../models/factoid";
import {
  PROJECTS_FAKE_JSON_DATA,
} from "../../../constants/routes/reports";
import { absoluteStoryhunterUrl } from "../../../utils/url";
import { BILLABLE_RECORD } from "../../../models/billable-record";
import FreelancerProjectAnalyticsTotals from 'sh-www/components/analytics/freelancers/freelancer-project-analytics-totals';

// parameters
const AMOUNT = 'amount';
const DATE = 'completedAt';
const MONTH = 'MONTH';
const PAID_STATUS_TEXT = 'paidStatusText';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];


export default BaseAnalytics.extend({
  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  shPaymentCommandService : service('commands/sh-payment-commands'),
  userCommandService : service('commands/user-commands'),
  paymentCommandService : service('commands/payment-commands'),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  ////////////
  // CONFIG //
  ////////////

  title: computed('year', function() {
    const year = this.get('year');
    if (!year) {
      return 'Earnings';
    }
    const dateString = (year === new Date().getFullYear()) ? 'YTD' : year;

    return `Earnings - ${dateString}`;
  }),

  factoidType: 'spend',
  annual: true,
  hideGraph: false,
  allowTableDownload: false,
  defaultFileName: computed('year', function() {
    const year = this.get('year');
    return `Glimmer_Projects_Report_${year}.csv`;
  }),

  componentAboveGraph: FreelancerProjectAnalyticsTotals,

  ////////////////
  // FORMATTING //
  ////////////////
  data: computed({
    set(_key, data) {
      let year = this.get('year'),
        endDate = new Date(year + 1, 0, 1),
        startDate = new Date(year, 0, 1);

      return data.map(shPayment => {
        let projectMetric = this.get('store').createRecord('freelancerProjectMetric'),
          title = shPayment.get('application.title');

        if (shPayment.get('type') === BILLABLE_RECORD.TYPES.PROJECT_ADVANCE) {
          title += ' (Advance)';
        }

        projectMetric.setProperties({
          'id': shPayment.get('id'),
          'title': title,
          'projectId': shPayment.get('application.id'),
          'completedAt': shPayment.get('dueAt'),
          'amount': shPayment.get('amount'),
          'paymentId': shPayment.get('id'),
          'buyerCompanyName': shPayment.get('application.buyer.company.name'),
          'startedAt': shPayment.get('createdAt'),
          'paidAt': shPayment.get('paidAt'),
          'payoutAt': shPayment.get('isPaid') ?
            shPayment.get('paidAt') :
            shPayment.get('estimatedPayoutDate'),
          'startDate': startDate,
          'endDate': endDate
        });

        const month = projectMetric.get(DATE).getMonth();
        projectMetric.set(MONTH, month);
        return projectMetric;
      });
    }
  }),

  ///////////
  // CHART //
  ///////////
  chartKeyParam: MONTH,
  initialSumParam: AMOUNT,
  sumParam: AMOUNT,
  initialGroupParam: PAID_STATUS_TEXT,
  groupParam: PAID_STATUS_TEXT,
  usesGroups: true,
  isShowGroupFilterLinks: false,

  /**
   * @inheritDoc
   */
  shouldFadeFullPage: readOnly('firstTimeLoading'),

  /**
   * @inheritDoc
   */
  shouldFadeDataVisuals: readOnly('noData'),

  /**
   * Object indexed by the Paid Status => Array of RGB values for the specific color.
   *
   * These indices should map to whatever we're using for the grouping data.
   *
   * @type {Object}
   */
  groupedChartGraphColors: EmberObject.create({
    [FREELANCER_PROJECT_METRIC.PAID_STATUS_TEXT.UPCOMING] : [169, 116, 255],
    [FREELANCER_PROJECT_METRIC.PAID_STATUS_TEXT.PAID] : [48, 241, 199]
  }),

  chartGroupParams: [
    {
      label: 'By Status',
      value: PAID_STATUS_TEXT,
      defaultParam: true,
    }
  ],

  chartLabels: computed('year', function() {
    const currentYear = new Date().getFullYear();
    const year = this.get('year');
    // for the current year we only want to show up to the current month
    const numMonths = (year === currentYear) ? new Date().getMonth() + 1 : 12;

    const labels = {};
    for (let month = 0; month < numMonths; month++) {
      labels[month] = MONTHS[month];
    }

    return labels;
  }),

  chartSumFormatter: computed('sumParam', function() {
    const sumParam = this.get('sumParam');
    switch (sumParam) {
      case 'amount':
        return this.formatMoneyWithoutChange.bind(this);
      default:
        return null;
    }
  }),

  ///////////
  // TABLE //
  ///////////
  tableColumns: computed(function() {
    const formatMoney = this.formatMoney.bind(this),
      formatDate = this.formatDate.bind(this),
      formatPaidStatusText = this._formatPaidStatusText;

    return [
      {
        label: 'Receipt #',
        csvLabel: 'Receipt ID',
        param: 'paymentId',
        sortDescending: true,
        defaultSort: true,
        display: invoiceId => invoiceId ? `#${invoiceId}` : 'None',
        sortFunction: (a, b) => parseInt(a) - parseInt(b)
      },
      {
        label: 'Date',
        csvLabel: 'Date',
        param: 'completedAt',
        display: formatDate
      },
      {
        label: 'Project #',
        csvLabel: 'Project ID',
        param: 'projectId',
      },
      {
        label: 'Project',
        param: 'title',
        wide: true,
      },
      {
        label: 'Company',
        param: 'buyerCompanyName',
        wide: true,
      },
      {
        label: 'Amount ($USD)',
        csvLabel: 'Amount',
        param: 'amount',
        display: formatMoney
      },
      {
        label: 'Payout Date',
        csvLabel: 'Payout Date',
        param: 'payoutAt',
        display: formatDate
      },
      {
        label: 'Status',
        csvLabel: 'Status',
        param: 'paidStatusText',
        display: formatPaidStatusText,
      },
      {
        param: 'paymentId',
        icon: 'file_download',
        action: paymentId => this.downloadPDF(paymentId)
      }
    ];
  }),

  //////////////
  // FACTOIDS //
  //////////////
  factoidLayout: computed('media.isSearchMobile', function() {
    const mobile = this.get('media.isSearchMobile');
    if (mobile) {
      return [
        [{ title: FACTOID.TITLE_TEXT.EARNINGS_YTD }, { title: FACTOID.TITLE_TEXT.PAID_YTD }],
        [{ title: FACTOID.TITLE_TEXT.UPCOMING_PAYOUTS }]
      ];
    }

    return [[
      { title: FACTOID.TITLE_TEXT.EARNINGS_YTD },
      { title: FACTOID.TITLE_TEXT.PAID_YTD },
      { title: FACTOID.TITLE_TEXT.UPCOMING_PAYOUTS }
    ]];
  }),

  /////////////
  // METHODS //
  /////////////
  queryData(year) {
    if (this.get('noData')) {
      let store = this.get('store');
      store.pushPayload(PROJECTS_FAKE_JSON_DATA);
      return Promise.resolve(this.get('store').peekAll('freelancerProjectMetric'));
    }

    return new Promise((resolve, reject) => {
      this.get('shPaymentCommandService').loadShPayments(
        1,
        500,
        {
          'user_id': this.get('currentUser.id')
        },
        [
          'application.buyer.company'
        ],
        new Date(year, 0, 1),
        new Date(year + 1, 0, 1),
        true
      ).then(responseObject => {
        resolve(responseObject.shPayments);
      }).catch(e => {
        reject(e);
      });
    });
  },

  peekData(year) {
    return this.get('store').peekAll('shPayments').filter(item => {
      return item.get('createdAt').getFullYear() === year;
    });
  },

  /**
   * Download a pdf for the given payment id
   *
   * @param {String} paymentId
   */
  downloadPDF: action(function(paymentId) {
    window.open(absoluteStoryhunterUrl(`/sh-payments/${paymentId}/pdf`));
  }),

  /**
   * Override this to return the correct factoids for freelancer projects view.
   *
   * @inheritDoc
   */
  _queryFactoids() {
    if (this.get('noData')) {
      return Promise.reject(false);
    }

    return this.get('userCommandService').getUserAnalyticsFactoids(
      this.get('currentUser.id'),
      FACTOID.TYPES.SELF);
  },

  /**
   * Get the HTML safe string for the status text for this metric's status.
   *
   * @param {String} statusText   of type FREELANCER_PROJECT_METRIC.PAID_STATUS_TEXT
   * @return {String}             HTML safe string to display.
   * @private
   */
  _formatPaidStatusText(statusText) {
    let statusTextClass = 'unknown';
    switch (statusText) {
      case FREELANCER_PROJECT_METRIC.PAID_STATUS_TEXT.UPCOMING:
        statusTextClass = 'upcoming';
        break;
      case FREELANCER_PROJECT_METRIC.PAID_STATUS_TEXT.PAID:
        statusTextClass = 'paid';
        break;
    }

    return htmlSafe(`<span class=${statusTextClass}>${statusText}</span>`);
  }
});
